import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import * as Sentry from '@sentry/react';
import './stripe.scss';

const getClientId = () => window.sessionStorage.locationId || 'unknown';

const PaymentForm = ({ onReady, onError, onElementsReady, onStripeReady, surcharging }) => {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    if (onStripeReady) {
      onStripeReady(stripe);
    }

    if (onElementsReady) {
      onElementsReady(elements);
    }

    const paymentElement = elements.getElement(PaymentElement);
    if (paymentElement) {
      paymentElement.on('ready', () => {
        if (onReady) onReady();
      });

      paymentElement.on('change', (event) => {
        if (event.complete) {
          if (onReady) onReady();
        } else if (event.error) {
          if (onError) onError(event.error);
        }
      });
    }
  }, [stripe, elements, onReady, onError, onElementsReady, onStripeReady]);

  const paymentElementOptions = surcharging
    ? { fields: { billingDetails: { address: { country: 'auto', postalCode: 'auto' } } } }
    : undefined;

  return (
    <div className="stripe-payment-form">
      <PaymentElement options={paymentElementOptions} />
    </div>
  );
};

const StripeAdapter = ({
  mode,
  amount,
  connectedAccountId,
  onReady,
  onError,
  onToken,
  onElementsReady,
  onStripeReady,
  surcharging,
}) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const publicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (!publicKey) {
      const err = new Error('Missing REACT_APP_STRIPE_PUBLIC_KEY environment variable');
      setError(err);
      if (onError) onError(err);
      return;
    }

    const options = connectedAccountId ? { stripeAccount: connectedAccountId } : {};
    setStripePromise(loadStripe(publicKey, options));
  }, [connectedAccountId, onError]);

  if (error) {
    return (
      <div className="stripe-error">
        <p>Unable to load payment form. Please refresh and try again.</p>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="stripe-loading">
        <p>Loading payment form...</p>
      </div>
    );
  }
  const options = {
    mode: mode, // 'payment' | 'setup'
    amount: mode === 'payment' ? amount : undefined,
    currency: 'usd',
    setupFutureUsage: mode === 'payment' ? 'off_session' : undefined,
    paymentMethodCreation: 'manual',
    paymentMethodTypes: ['card'],
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#14CEFB',
        colorBackground: '#ffffff',
        colorText: '#374151',
        colorDanger: '#df1b41',
        fontFamily: 'Inter, system-ui, Arial, sans-serif',
        spacingUnit: '4px',
        borderRadius: '10px',
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentForm
        onReady={onReady}
        onError={onError}
        onElementsReady={onElementsReady}
        onStripeReady={onStripeReady}
        surcharging={surcharging}
      />
    </Elements>
  );
};

export default StripeAdapter;

export const createConfirmationToken = async (stripe, elements, options = {}) => {
  if (!stripe || !elements) {
    Sentry.captureMessage('Stripe not initialized for confirmation token', {
      level: 'error',
      tags: { component: 'StripeAdapter', method: 'createConfirmationToken' },
      extra: { clientId: getClientId() },
    });
    throw new Error('Stripe not initialized');
  }

  const tokenOptions = {
    elements,
    ...options
  };

  const { error, confirmationToken } = await stripe.createConfirmationToken(tokenOptions);

  if (error) {
    throw error;
  }

  return confirmationToken;
};

export const createPaymentMethod = async (stripe, elements) => {
  if (!stripe || !elements) {
    Sentry.captureMessage('Stripe not initialized for payment method creation', {
      level: 'error',
      tags: { component: 'StripeAdapter', method: 'createPaymentMethod' },
      extra: { clientId: getClientId() },
    });
    throw new Error('Stripe not initialized');
  }

  const { error: submitError } = await elements.submit();
  if (submitError) {
    throw submitError;
  }

  const { error, paymentMethod } = await stripe.createPaymentMethod({ elements });

  if (error) {
    throw error;
  }

  return paymentMethod;
};

export const confirmPayment = async (
  stripe,
  clientSecret,
  paymentIntentId,
  { surcharge = 0 } = {},
) => {
  if (!stripe) {
    Sentry.captureMessage('Stripe not initialized for payment confirmation', {
      level: 'error',
      tags: { component: 'StripeAdapter', method: 'confirmPayment' },
      extra: { clientId: getClientId(), paymentIntentId },
    });
    throw new Error('Stripe not initialized');
  }

  const confirmParams =
    surcharge > 0
      ? {
          payment_method_options: {
            card: { surcharge: { consent: { collection: 'enabled' } } },
          },
        }
      : undefined;

  const { error, paymentIntent } = await stripe.confirmPayment({
    clientSecret,
    redirect: 'if_required',
    ...(confirmParams && { confirmParams }),
  });

  if (error) {
    throw error;
  }

  return paymentIntent;
};

export const confirmSetup = async (stripe, clientSecret, setupIntentId) => {
  if (!stripe) {
    Sentry.captureMessage('Stripe not initialized for setup confirmation', {
      level: 'error',
      tags: { component: 'StripeAdapter', method: 'confirmSetup' },
      extra: { clientId: getClientId(), setupIntentId },
    });
    throw new Error('Stripe not initialized');
  }

  const { error, setupIntent } = await stripe.confirmSetup({
    clientSecret,
    redirect: 'if_required',
  });

  if (error) {
    throw error;
  }

  return setupIntent;
};
